import { useEffect } from "react";
import { motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

const ROWS = [
  ["hook", "Hook"],
  ["summary", "İçerik Özeti"],
  ["purpose", "Amaç"],
  ["emotion", "Verilen Duygu"],
  ["trust_element", "Oluşturulan Güven Unsuru"],
  ["psychological_trigger", "Psikolojik Tetikleyici"],
  ["brand_contribution", "Marka Katkısı"],
  ["loyalty_contribution", "Sadakat Katkısı"],
  ["whatsapp_contribution", "WhatsApp Sipariş Katkısı"],
  ["behavior_change", "Beklenen Davranış Değişikliği"],
  ["target_outcome", "Hedeflenen Sonuç"],
];

export const ContentModal = ({ content, contents = [], onClose, onNavigate }) => {
  const idx = contents.findIndex((c) => c.slug === content.slug);
  const total = contents.length;
  const prev = idx > 0 ? contents[idx - 1] : null;
  const next = idx < total - 1 ? contents[idx + 1] : null;

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft" && prev) onNavigate(prev);
      else if (e.key === "ArrowRight" && next) onNavigate(next);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [prev, next, onClose, onNavigate]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="trust-root fixed inset-0 z-[70] bg-[#050505]/95 backdrop-blur-2xl flex flex-col md:flex-row overflow-hidden"
      data-testid="trust-modal"
    >
      <button
        onClick={onClose}
        data-testid="trust-modal-close"
        aria-label="Kapat"
        className="absolute top-5 right-5 z-30 w-11 h-11 flex items-center justify-center rounded-full border border-white/10 bg-black/40 text-[#F5F5F0] hover:border-[#C48A5E] hover:text-[#C48A5E] transition-colors"
      >
        <X className="w-5 h-5" />
      </button>

      {/* Önceki / Sonraki ok butonları */}
      <button
        onClick={() => prev && onNavigate(prev)}
        disabled={!prev}
        data-testid="trust-modal-prev"
        aria-label="Önceki içerik"
        className="absolute left-2 md:left-5 top-1/2 -translate-y-1/2 z-30 w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full border border-white/10 bg-black/50 text-[#F5F5F0] transition-all hover:border-[#C48A5E] hover:text-[#C48A5E] hover:-translate-x-0.5 disabled:opacity-25 disabled:pointer-events-none"
      >
        <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
      </button>
      <button
        onClick={() => next && onNavigate(next)}
        disabled={!next}
        data-testid="trust-modal-next"
        aria-label="Sonraki içerik"
        className="absolute right-2 md:right-5 top-1/2 -translate-y-1/2 z-30 w-11 h-11 md:w-12 md:h-12 flex items-center justify-center rounded-full border border-white/10 bg-black/50 text-[#F5F5F0] transition-all hover:border-[#C48A5E] hover:text-[#C48A5E] hover:translate-x-0.5 disabled:opacity-25 disabled:pointer-events-none"
      >
        <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
      </button>

      <div className="w-full md:w-1/2 h-[38vh] md:h-full relative bg-black shrink-0">
        <motion.img
          key={`img-${content.slug}`}
          initial={{ opacity: 0, scale: 1.04 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          src={content.media}
          alt={content.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-[#050505] via-transparent to-transparent opacity-70" />
      </div>

      <motion.div
        key={`panel-${content.slug}`}
        initial={{ x: 24, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="w-full md:w-1/2 h-[62vh] md:h-full overflow-y-auto p-8 md:p-12 lg:p-16"
      >
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] tracking-[0.3em] text-[#C48A5E] uppercase">
            İçerik #{String(content.idx).padStart(2, "0")} · Stratejik Dosya
          </span>
          <span className="font-mono text-[10px] tracking-[0.2em] text-[#A3A09A]" data-testid="trust-modal-counter">
            {idx + 1} / {total}
          </span>
        </div>
        <h3 className="mt-4 font-serif text-3xl md:text-4xl text-[#F5F5F0]">{content.title}</h3>

        <div className="mt-5 flex flex-wrap gap-2">
          {(content.tags || []).map((t) => (
            <span key={t} className="trust-pill" data-testid={`trust-modal-tag-${t}`}>
              {t}
            </span>
          ))}
        </div>

        <div className="mt-8 space-y-6">
          {ROWS.map(([k, label]) =>
            content[k] ? (
              <div key={k} className="border-t border-white/5 pt-4">
                <div className="font-mono text-[10px] tracking-[0.25em] text-[#C48A5E]/80 uppercase mb-2">
                  {label}
                </div>
                <p className="font-sans text-[15px] leading-relaxed text-[#D6D3CD]">{content[k]}</p>
              </div>
            ) : null
          )}
        </div>

        {/* Alt gezinme (mobil dostu) */}
        <div className="mt-10 flex items-center justify-between border-t border-white/5 pt-6">
          <button
            onClick={() => prev && onNavigate(prev)}
            disabled={!prev}
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[#A3A09A] hover:text-[#C48A5E] transition-colors disabled:opacity-25 disabled:pointer-events-none"
          >
            <ChevronLeft className="w-4 h-4" /> Önceki
          </button>
          <button
            onClick={() => next && onNavigate(next)}
            disabled={!next}
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[#A3A09A] hover:text-[#C48A5E] transition-colors disabled:opacity-25 disabled:pointer-events-none"
          >
            Sonraki <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ContentModal;
